import type { SessionListItem } from '@/features/sessions/types';
import type { ObjectWorkspaceObject } from '@heidi/agents';
import { calculateAge } from '@heidi/patients';
import type { IntlShape } from 'react-intl';

import { getRecentVisitPrimaryLabel } from '../recent-visits/recent-visit-label';
import type { PatientIdentityProfile } from './object-workspace-patient-details';
import type { WorkspaceSession } from './use-object-workspace-session-lookup';

// Same precedence as `resolvePatientName`, which only accepts a full list row.
const resolveSessionPatientName = (session: WorkspaceSession): string | undefined =>
  session.patient_profile_name?.trim() || session.patient.name?.trim();

export const toVisitObject = (
  session: WorkspaceSession,
  intl: IntlShape,
  patientProfiles: readonly PatientIdentityProfile[] = []
): ObjectWorkspaceObject | null => {
  // Same label the sidebar row that opens this workspace shows: the session
  // name, or the untitled fallback — never a timestamp.
  const name = getRecentVisitPrimaryLabel(session, intl).trim();
  if (!name) return null;

  const patientProfileId = session.patient_profile_id?.trim();
  if (!patientProfileId) return { id: session.session_id, kind: 'visit', name };

  const patientProfile = patientProfiles.find(
    (profile) => profile.id === patientProfileId
  );
  const profileName = patientProfile
    ? [patientProfile.first_name, patientProfile.last_name]
        .filter(Boolean)
        .join(' ')
        .trim() || patientProfile.demographic_string.trim()
    : '';
  const patientName =
    (profileName && profileName !== '—'
      ? profileName
      : resolveSessionPatientName(session)) ||
    intl.formatMessage({
      defaultMessage: 'Linked patient',
      id: 'VJywyrGGho',
      description:
        'Fallback patient label for an encounter whose linked profile name is unavailable',
    });
  const gender = patientProfile?.gender?.trim();
  const age = calculateAge(patientProfile?.birth_date);

  return {
    id: session.session_id,
    kind: 'visit',
    name,
    patient: {
      id: patientProfileId,
      name: patientName,
      sourceSessionId: session.session_id,
      patientProfileId,
      ...(gender && gender !== 'unknown' ? { sex: gender } : {}),
      ...(age !== null ? { age } : {}),
    },
  };
};

export const toPatientObject = (
  session: WorkspaceSession
): ObjectWorkspaceObject | null => {
  const name = resolveSessionPatientName(session);
  if (!name) return null;
  const patientProfileId = session.patient_profile_id?.trim() || undefined;
  return {
    id: patientProfileId ?? session.session_id,
    kind: 'patient',
    name,
    sourceSessionId: session.session_id,
    patientProfileId,
  };
};

export const uniqueObjects = (
  objects: readonly (ObjectWorkspaceObject | null)[]
): ObjectWorkspaceObject[] => {
  const seen = new Set<string>();
  return objects.flatMap((object) => {
    if (!object) return [];
    const key = `${object.kind}:${object.id}`;
    if (seen.has(key)) return [];
    seen.add(key);
    return [object];
  });
};

export const uniqueSessions = (
  sessions: readonly SessionListItem[]
): SessionListItem[] => {
  const seen = new Set<string>();
  return sessions.filter((session) => {
    if (seen.has(session.session_id)) return false;
    seen.add(session.session_id);
    return true;
  });
};
